import { Parser, Writer } from 'n3'
import { assertNever } from '@/utils/assert-never'
import { CodeEditorMode } from './code-editor'

export type ModeAbilities = {
  label: string
  mimeType: string
  extension: string
  fileAccept: string | undefined
  parse: ((value: string) => void) | undefined
  /** `undefined` if formatting is not possible yet */
  format: ((value: string, onFormatted: (formatted: string) => void) => void) | undefined
}

export function getModeAbilities(mode: CodeEditorMode): ModeAbilities {
  switch (mode) {
    case 'json':
      return {
        label: 'JSON',
        mimeType: 'application/json',
        extension: 'json',
        fileAccept: '.json',
        parse: value => void JSON.parse(value),
        format: (value, onFormatted) => onFormatted(JSON.stringify(JSON.parse(value), null, 4)),
      }
    case 'turtle':
      return {
        label: 'Turtle',
        mimeType: 'text/turtle',
        extension: 'ttl',
        fileAccept: '.ttl',
        parse: value => void new Parser().parse(value),
        format: formatTurtle,
      }
    case 'sparql':
      return {
        label: 'SPARQL',
        mimeType: 'application/sparql-query',
        extension: 'sparql',
        fileAccept: '.sparql,.rq',
        parse: undefined,
        format: undefined,
      }
    case 'javascript':
      return {
        label: 'JavaScript',
        mimeType: 'application/javascript',
        extension: 'js',
        fileAccept: '.js',
        parse: undefined,
        format: undefined,
      }
    case 'html':
      return {
        label: 'HTML',
        mimeType: 'text/html',
        extension: 'html',
        fileAccept: '.html,.htm',
        parse: undefined,
        format: undefined,
      }
    default:
      return assertNever(mode)
  }
}

function formatTurtle(value: string, onFormatted: (formatted: string) => void) {
  const quads = new Parser().parse(value)

  // retain the prefixes the author wrote, as N3 does not report them
  const prefixes: Record<string, string> = {}
  const prefixRegex = /@prefix\s+(\w*):?\s*<([^>]+)>\s*\./gi
  let match
  while ((match = prefixRegex.exec(value)) !== null) {
    prefixes[match[1]] = match[2]
  }

  const writer = new Writer({ prefixes })
  writer.addQuads(quads)
  writer.end((error, result) => {
    if (error) return console.error('Error formatting Turtle:', error)
    onFormatted(result)
  })
}
